import React from 'react';

import Image from '$partials/media/image';
import Button from '$partials/ui/button.jsx';
import { resolvePath } from '$utilities/routes';
import { IconChevronSprite } from '$partials/icons/index.js';
import { useLocale } from '$contexts/locale.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/modules.d.ts').BentoCardItemContent}
 */
const CardItemContent = ({
    heading,
    pageTitle,
    cfMetadata,
    eyebrow,
    brandLogo,
    bodyCopy,
    ctaLabel,
    listingCtaLabel,
    slug,
    ctaLink,
    image,
    __typename,
    stretchHeight
}) => {
    const { formatURI } = useLocale();

    return (
        __typename === 'BvaVerifiedArticleDetailPage' ?
            (
                <div className={`p-24 md:p-32 rounded bg-surface-raised hover:bg-surface-raised-hover flex flex-col${stretchHeight ? ' md:h-full md:justify-between' : ''}`}>
                    <div className='flex flex-col'>
                        {brandLogo?.src && (
                            <Image
                                className='h-40 w-auto object-contain'
                                containerClassName='self-start mb-24'
                                src={brandLogo?.src}/>
                        )}
                        <h4 className='type-style-4 text-text-primary mb-4'>
                            {pageTitle}
                        </h4>

                        {bodyCopy && (
                            <p className='type-style-body text-text-secondary'>
                                {bodyCopy}
                            </p>
                        )}
                    </div>
                    {listingCtaLabel && slug && cfMetadata && (
                        <div className='mt-40 md:mt-77 flex self-start'>
                            <Button
                                as='a'
                                className='link link--large-brand'
                                href={formatURI(resolvePath({ slug: slug, cfMetadata: cfMetadata }))}>
                                <span className='button__label--animate'>
                                    <span>
                                        {listingCtaLabel}
                                    </span>
                                </span>
                                <span className='button__icon button__icon--animate'>
                                    <IconChevronSprite />
                                </span>
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                // BvaImageCardItem
                <div className='overflow-hidden pt-24 md:pt-32 rounded flex flex-col bg-surface-raised h-full justify-between'>
                    <div className='flex flex-col grow pb-24 md:pb-32 px-24 md:px-32'>
                        {eyebrow && (
                            <p
                                className='type-style-eyebrow text-text-accent uppercase mb-16'>
                                {eyebrow}
                            </p>
                        )}
                        <h4 className='type-style-4 text-text-primary mb-4'>
                            {heading}
                        </h4>

                        {bodyCopy && (
                            <p className='type-style-body text-text-secondary'>
                                {bodyCopy}
                            </p>
                        )}
                        {ctaLabel && ctaLink && (
                            <div className='mt-16 flex self-start'>
                                <Button
                                    as='a'
                                    className='button button--secondary-alt'
                                    href={formatURI(ctaLink)}>
                                    <span className='button__label--animate'>
                                        <span>{ctaLabel}</span>
                                    </span>
                                    <span className='button__icon button__icon--animate'>
                                        <IconChevronSprite />
                                    </span>
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className='overflow-hidden aspect-video pointer-events-none select-none'>
                        {image?.contentType?.includes('video/') ? (
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                src={image?.src}
                                width={'100%'} />
                        ) :
                            <Image
                                className='h-full object-cover'
                                containerClassName='h-full object-cover'
                                height={'100%'}
                                loadWidth={840}
                                src={image?.src}
                                width={'100%'} />
                        }
                    </div>
                </div>
            )
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').ModuleBento}
 */

const ModuleBento = ({
    bodyCopy,
    eyebrow,
    title,
    itemsCollection,
    isArticlePage,
}) => {
    const items = itemsCollection?.items ?? [];
    const imageCards = items.filter(i => i.__typename === 'BvaImageCardItem');
    const articles = items.filter(i => i.__typename === 'BvaVerifiedArticleDetailPage');

    const renderGrid = () => {
        // 1 image + 2+ case studies: image fills full left column, case studies stack in right column
        if (imageCards.length === 1 && articles.length > 1) {
            return (
                <div className='flex col-wrapper mt-50'>
                    <div className='col-padding w-full md:w-6/12'>
                        <CardItemContent {...imageCards[0]} />
                    </div>
                    <div className='col-padding w-full md:w-6/12 flex flex-col gap-y-16'>
                        {articles.map((cs, i) => (
                            <div
                                className='flex-1 flex flex-col'
                                key={i}>
                                <CardItemContent
                                    stretchHeight
                                    {...cs} />
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // Default: 2-column flex wrap. Case studies stretch when their row partner is an image card
        // (covers: 1+1, 2+2, 3+1, and any other combination)
        return (
            <div className='flex flex-wrap mt-50 gap-y-16 col-wrapper'>
                {/** @type {any[]} */ (items).map((itm, i) => {
                    const partnerIndex = i % 2 === 0 ? i + 1 : i - 1;
                    const stretchHeight = itm.__typename === 'BvaVerifiedArticleDetailPage' &&
                        items[partnerIndex]?.__typename === 'BvaImageCardItem';
                    return (
                        <div
                            className='col-padding w-full md:w-6/12'
                            key={i}>
                            <CardItemContent
                                stretchHeight={stretchHeight}
                                {...itm} />
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <section className={`relative py-40${isArticlePage ? '' : ' lg:py-80'}`}>
            <div className='layout-container layout-site-outer-padding'>
                <div className='flex flex-col sm:flex-row gap-y-24 justify-between col-wrapper'>
                    <div className='col-padding sm:w-10/12 md:w-7/12 lg:w-6/12'>
                        <div className='bg-white'>
                            {eyebrow && (
                                <p
                                    className='type-style-eyebrow text-text-accent mb-12'>
                                    {eyebrow}
                                </p>
                            )}

                            <h2 className={`${isArticlePage ? 'type-style-4' : 'type-style-3-extended'} text-text-primary mb-10`}>
                                {title}
                            </h2>

                            {bodyCopy && (
                                <p className='type-style-body text-text-secondary'>
                                    {bodyCopy}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                {items.length > 0 && renderGrid()}
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default ModuleBento;
